import { useEffect, useState } from 'react'
import { Alert, App, Modal, Space, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { setUserPartyNode, type PanelUser } from './identityApi'
import { OrgUnitTreeSelect } from './OrgUnitTreeSelect'
import { useOrgUnits } from './useOrgUnits'

interface PartyNodeModalProps {
  open: boolean
  user: PanelUser | null
  onClose: () => void
}

/**
 * Hisobni tashkiliy tuzilmaga bog'lash.
 *
 * <h2>⚠️ BU OYNA BIRLIK YARATMAYDI</h2>
 * Egasi qarori (2026-08-22): tuzilma FAQAT «Tuzilma» menyusida
 * yaratiladi. Bu yerdan yaratishga yo'l ochilsa daraxt ikki joydan
 * o'sardi va "qaysi biri to'g'ri" degan javobsiz savol paydo bo'lardi.
 * Shuning uchun bu yerda faqat MAVJUD birlik tanlanadi, ro'yxat bo'sh
 * bo'lsa esa Tuzilma sahifasiga havola beriladi.
 *
 * <h2>⚠️ TANLANADIGAN BIRLIK TURI BO'YICHA CHEKLANMAYDI</h2>
 * `useEmployeeNodes` dagi `hasEmployees` filtri bu yerda NOTO'G'RI
 * bo'lardi: sherik (agent/reseller) birliklarida u `false`, ya'ni
 * sherikni umuman bog'lab bo'lmasdi — vaholanki egasi aynan
 * «agent, reseller, xodim» deb aytdi. Server ham turni tekshirmaydi
 * (`UserServiceImpl#setPartyNode` javadoc'i), ya'ni bu yerdagi qo'shimcha
 * cheklov faqat panelning O'Z to'sig'i bo'lardi va u serverda yo'q
 * qoidani ixtiro qilardi.
 *
 * Yagona cheklov — YOPILGAN birlik: unga yangi bog'lanish bermaydi.
 * Istisno bitta va u zarur: hisob ALLAQACHON yopilgan birlikka
 * bog'langan bo'lsa, u daraxtda ko'rinadi — aks holda oyna joriy
 * qiymatni bo'sh ko'rsatib, saqlashda uni jimgina uzib yuborardi.
 *
 * <h2>⚠️ UZISH — ATAYLAB, JIMGINA EMAS</h2>
 * `null` server uchun haqiqiy qiymat: bog'lanishni uzadi. Shuning uchun
 * tanlov tozalanganda ogohlantirish chiqadi va tugma matni o'zgaradi —
 * operator nima qilayotganini KO'RIB tursin.
 */
export function PartyNodeModal({ open, user, onClose }: PartyNodeModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { can } = usePermissions()
  const queryClient = useQueryClient()

  const { units, types, loading, reliable, reason, truncated, pathOf } = useOrgUnits(open)

  const current = user?.partyNodeId ?? null
  const [value, setValue] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setValue(current)
  }, [open, current])

  const save = useMutation({
    mutationFn: (next: string | null) => setUserPartyNode(user?.id ?? '', next),
    onSuccess: () => {
      /*
        ⚠️ IKKALASI HAM: ro'yxat va ochiq kartochka ALOHIDA so'rov.
        Faqat ro'yxatni bekor qilish kartochkani eski bog'lanish bilan
        qoldirardi — operator "saqlanmadi" deb o'ylardi.
      */
      void queryClient.invalidateQueries({ queryKey: queryKeys.identity.users })
      if (user !== null) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.identity.user(user.id) })
      }
      void message.success(
        value === null ? t('identity.partyNode.unlinkDone') : t('identity.partyNode.linkDone'),
      )
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  const chosenPath = pathOf(value)
  const currentPath = pathOf(current)
  const unchanged = value === current
  const unlinking = value === null && current !== null

  return (
    <Modal
      open={open}
      title={t('identity.partyNode.modalTitle')}
      onCancel={close}
      onOk={() => save.mutate(value)}
      okText={unlinking ? t('identity.partyNode.unlink') : t('common.save')}
      okButtonProps={{ danger: unlinking, disabled: unchanged || !reliable }}
      cancelText={t('common.cancel')}
      confirmLoading={save.isPending}
      destroyOnHidden
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('identity.partyNode.modalIntro', { username: user?.username ?? '' })}
        </Typography.Paragraph>

        {/* JORIY holat — o'zgartirishdan OLDIN nima borligini ko'rsatadi.
            UUID emas, nom: operator tanlovni ham nom bo'yicha qiladi. */}
        <Alert
          type="info"
          showIcon
          message={t('identity.partyNode.currentTitle')}
          description={
            current === null
              ? t('identity.partyNode.currentNone')
              : (currentPath ??
                t('identity.partyNode.currentUnknown'))
          }
        />

        {reason === 'no-permission' ? (
          <Alert type="warning" showIcon message={t('identity.partyNode.needOrgRead')} />
        ) : null}
        {reason === 'failed' ? (
          <Alert type="error" showIcon message={t('identity.partyNode.loadFailed')} />
        ) : null}
        {truncated ? (
          <Alert type="warning" showIcon message={t('identity.partyNode.truncated')} />
        ) : null}

        <OrgUnitTreeSelect
          units={units}
          types={types}
          value={value}
          onChange={setValue}
          currentId={current}
          allowClear
          disabled={!reliable}
          loading={loading}
        />

        {chosenPath !== null && !unchanged ? (
          <Typography.Text type="secondary">
            {t('identity.partyNode.chosen', { path: chosenPath })}
          </Typography.Text>
        ) : null}

        {/* Uzish HECH QANDAY xato bermaydi va oqibati faqat keyin
            bilinadi: odam kiradi va bo'sh ekran ko'radi. */}
        {unlinking ? (
          <Alert
            type="warning"
            showIcon
            message={t('identity.partyNode.unlinkWarnTitle')}
            description={t('identity.partyNode.unlinkWarnText')}
          />
        ) : null}

        {/* ⛔ Bu oyna birlik YARATMAYDI — yagona joy Tuzilma sahifasi.
            Havola faqat huquqi borga: aks holda u "kirish yo'q"
            sahifasiga olib borardi. */}
        {can('org.node.read') ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('identity.partyNode.createElsewhere')}{' '}
            <Link to="/org/structure">{t('identity.partyNode.openStructure')}</Link>
          </Typography.Text>
        ) : null}

        {/* ⚠️ Xato OYNANING ICHIDA — u yopilib ketsa operator "bosdim,
            hech narsa bo'lmadi" degan xulosaga kelardi va qayta bosardi.
            Yopishda mutatsiya tozalanadi — eski xabar keyingi
            ochilishda begona bo'lib chiqmasin. */}
        {save.isError ? (
          <Alert type="error" showIcon message={translateError(save.error, t)} />
        ) : null}
      </Space>
    </Modal>
  )
}
